// Reusable receipt — extracted from the original Billing page so any
// screen that records a payment (Billing, CheckInOut walk-in, CheckInOut
// checkout, Reservations) can pop the SAME printable receipt.
//
// Exports
// -------
//  • ReceiptDialog     — full modal with Print + Save-as-PDF buttons.
//  • ReceiptBody       — the printable surface (used inside the modal,
//                        exported for callers that want to compose it
//                        inline, e.g. an export route).
//  • openReceiptFor    — small helper that fetches the latest
//                        reservation detail + payment ledger and calls
//                        your `setReceipt` setter with the payload the
//                        dialog expects.
//
// The dialog expects a `{ reservation, payment, allPayments, facility }`
// shape — callers pre-load that before opening.

import React, { useRef } from "react";
import axios from "axios";
import { reservationByIdUrl } from "../BaseURL";
import { downloadHtmlAsPdf, printNode } from "../utils/exporters";

// ─── Receipt dialog (modal shell) ───────────────────────────────────────
export function ReceiptDialog({ reservation, payment, allPayments = [], facility, onClose }) {
    const printableRef = useRef(null);

    const code  = reservation?.code || "—";
    const title = `Receipt ${code}`;

    const handlePrint = () => printNode(printableRef.current, title);
    const handlePdf   = () => downloadHtmlAsPdf(printableRef.current?.outerHTML || "", title);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box"
                 style={{ width: "min(600px, 96vw)", maxHeight: "92vh", display: "flex", flexDirection: "column" }}
                 onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <span className="modal-title">Receipt · {code}</span>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                <div style={{
                    padding: "12px 18px", borderBottom: "1px solid rgba(180,140,80,.18)",
                    display: "flex", gap: 8, justifyContent: "flex-end",
                }}>
                    <button className="btn-outline" onClick={handlePrint}
                            style={{ padding: "7px 12px", fontSize: 11.5 }}>
                        🖨️ Print
                    </button>
                    <button className="btn-outline" onClick={handlePdf}
                            style={{ padding: "7px 12px", fontSize: 11.5 }}>
                        📄 Save as PDF
                    </button>
                </div>

                <div style={{ overflowY: "auto", padding: 18, background: "#f5ede2" }}>
                    <ReceiptBody
                        ref={printableRef}
                        reservation={reservation}
                        allPayments={allPayments}
                        facility={facility}
                        highlightPaymentId={payment?.id}
                    />
                </div>
            </div>
        </div>
    );
}

// ─── Printable surface ──────────────────────────────────────────────────
export const ReceiptBody = React.forwardRef(function ReceiptBody(
    { reservation, allPayments = [], facility, highlightPaymentId },
    ref
) {
    if (!reservation) return null;

    const total   = Number(reservation.total_amount) || 0;
    const paid    = allPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0)
                  || Number(reservation.paid_amount) || 0;
    const balance = Math.max(0, total - paid);

    // Build a single line item describing the stay.
    const mode    = reservation.billing_mode || "nightly";
    const units   = mode === "hourly" ? Number(reservation.hours)
                  : mode === "daily"  ? Number(reservation.nights)
                                      : Number(reservation.nights);
    const rate    = mode === "hourly" ? Number(reservation.rate_per_hour)
                  : mode === "daily"  ? Number(reservation.rate_per_day) || Number(reservation.rate_per_night)
                                      : Number(reservation.rate_per_night);
    const unitLbl = mode === "hourly" ? "hour"
                  : mode === "daily"  ? "day"
                                      : "night";

    return (
        <div ref={ref} style={{
            background: "#fff", borderRadius: 10,
            border: "1px solid rgba(180,140,80,.22)",
            padding: "26px 30px",
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
            color: "#2c1f14",
        }}>
            {/* Brand header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
                <div>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, color: "#2c1f14", fontWeight: 500 }}>
                        {facility?.name || "Hotel"}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b5142", marginTop: 3, lineHeight: 1.5 }}>
                        {[facility?.location, facility?.region].filter(Boolean).join(" · ")}<br/>
                        {[facility?.phone, facility?.email].filter(Boolean).join(" · ")}
                        {facility?.digital_address ? <><br/>{facility.digital_address}</> : null}
                    </div>
                </div>
                <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, letterSpacing: ".18em", textTransform: "uppercase", color: "#8a6a30", fontWeight: 600 }}>
                        Receipt
                    </div>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "#2c1f14", fontWeight: 500, marginTop: 2 }}>
                        {reservation.code}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b5142", marginTop: 2 }}>
                        Issued {fmtDateTime(new Date())}
                    </div>
                </div>
            </div>

            <Divider/>

            {/* Bill-to */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, margin: "16px 0" }}>
                <div>
                    <ReceiptLabel>Billed to</ReceiptLabel>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{reservation.guest_name}</div>
                    {reservation.guest_phone && <div style={{ fontSize: 12, color: "#6b5142" }}>{reservation.guest_phone}</div>}
                    {reservation.guest_email && <div style={{ fontSize: 12, color: "#6b5142" }}>{reservation.guest_email}</div>}
                </div>
                <div>
                    <ReceiptLabel>Stay</ReceiptLabel>
                    <div style={{ fontSize: 13 }}>
                        Room {reservation.room_number || "—"}
                        {reservation.room_type_name ? ` · ${reservation.room_type_name}` : ""}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b5142" }}>
                        {fmtDateTime(reservation.check_in_at  || reservation.check_in_date)} <br/>
                        → {fmtDateTime(reservation.check_out_at || reservation.check_out_date)}
                    </div>
                </div>
            </div>

            {/* Charges */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, fontSize: 12.5 }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Description</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Qty</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Rate</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={tdStyle}>
                            <div style={{ fontWeight: 500 }}>Room {reservation.room_number}</div>
                            <div style={{ fontSize: 11, color: "#6b5142" }}>
                                {reservation.room_type_name || "Stay"} · {mode}
                            </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                            {units} {unitLbl}{units === 1 ? "" : "s"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{fmtMoney(rate)}</td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 500 }}>{fmtMoney(total)}</td>
                    </tr>
                </tbody>
            </table>

            {/* Totals */}
            <div style={{ marginLeft: "auto", maxWidth: 260, fontSize: 13 }}>
                <Row label="Sub-total" value={fmtMoney(total)} />
                <Row label="Paid"      value={fmtMoney(paid)}  positive />
                <Row label="Balance"   value={fmtMoney(balance)} bold danger={balance > 0} bigger />
            </div>

            {/* Payments ledger */}
            {allPayments.length > 0 && (
                <>
                    <div style={{ height: 1, background: "rgba(180,140,80,.22)", margin: "20px 0 10px" }}/>
                    <ReceiptLabel>Payments received</ReceiptLabel>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 6 }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Date</th>
                                <th style={thStyle}>Method</th>
                                <th style={thStyle}>Reference</th>
                                <th style={{ ...thStyle, textAlign: "right" }}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allPayments.map(p => (
                                <tr key={p.id}
                                    style={p.id === highlightPaymentId ? { background: "rgba(200,160,90,.10)" } : null}>
                                    <td style={tdStyle}>{fmtDateTime(p.received_at)}</td>
                                    <td style={tdStyle}>{p.method}</td>
                                    <td style={tdStyle}>{p.reference || "—"}</td>
                                    <td style={{ ...tdStyle, textAlign: "right" }}>{fmtMoney(p.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}

            <div style={{ marginTop: 28, textAlign: "center", fontSize: 11, color: "#8a7060", fontStyle: "italic" }}>
                Thank you for staying with us. We hope to host you again.
            </div>
        </div>
    );
});

// ─── Fetch helper: load reservation + payments and open the dialog ──────
// Useful when the caller only knows a reservation id (or a stale snapshot).
// Falls back to the snapshot if the detail fetch fails so the operator
// still gets *some* receipt rather than a blank screen.
export async function openReceiptFor(reservation, setReceipt) {
    if (!reservation?.id) return;
    try {
        const detail = await axios.get(reservationByIdUrl(reservation.id));
        setReceipt({
            reservation: detail.data?.reservation || reservation,
            payment:     (detail.data?.payments || []).slice(-1)[0] || null,
            allPayments: detail.data?.payments || [],
        });
    } catch (_) {
        setReceipt({ reservation, payment: null, allPayments: [] });
    }
}

// ─── Tiny presentation helpers (private to this file) ───────────────────
function Row({ label, value, bold, danger, positive, bigger }) {
    return (
        <div style={{
            display: "flex", justifyContent: "space-between",
            padding: "5px 0",
            borderTop: bold ? "1.5px solid rgba(180,140,80,.4)" : "none",
            marginTop: bold ? 6 : 0,
            fontSize: bigger ? 15 : 13,
            fontWeight: bold ? 600 : 400,
            color: danger ? "#a93226" : positive ? "#2d6a4f" : "#3a2a1a",
        }}>
            <span style={{ color: "#6b5142", fontWeight: 400 }}>{label}</span>
            <span>{value}</span>
        </div>
    );
}

function Divider() {
    return <div style={{ height: 1, background: "rgba(180,140,80,.22)" }}/>;
}

function ReceiptLabel({ children }) {
    return (
        <div style={{
            fontSize: 9.5, letterSpacing: ".18em", textTransform: "uppercase",
            color: "#8a6a30", fontWeight: 600, marginBottom: 4,
        }}>{children}</div>
    );
}

const thStyle = {
    padding: "8px 6px",
    borderBottom: "1px solid rgba(180,140,80,.25)",
    textAlign: "left",
    fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
    color: "#8a6a30", fontWeight: 600,
};
const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid rgba(180,140,80,.12)",
    fontSize: 12.5, color: "#2c1f14",
};

const fmtMoney = (n) => `GH₵ ${Number(n || 0).toFixed(2)}`;
const fmtDateTime = (iso) => iso
    ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";
